import React, { useEffect, useRef, useState } from 'react'
import Modal from 'react-bootstrap/Modal'
import { promptWithdrawError, promptWithdrawSuccess } from './packageSubmissionPrompts'
import { Portal } from 'components/utility/portal'

function WithdrawalModal({ open = false, children, onClose = () => {} }) {
  const [show, setShow] = useState(open)
  const overlayRef = useRef(null)

  const handleClickOutsideModal = (e) => {
    if (e.target !== overlayRef.current) return
    onClose()
  }

  useEffect(() => setShow(open), [open])

  useEffect(() => {
    if (!show) return
    document.body.classList.toggle('overlay-activated', show)
    return () => {
      document.body.classList.remove('overlay-activated')
    }
  }, [show])

  if (!show) return null

  return (
    <Portal>
      <div className="widthdraw-flow-modal dome-full-width dome-full-height">
        <div
          ref={overlayRef}
          onClick={handleClickOutsideModal}
          className="widthdraw-flow-modal-backdrop dome-full-width dome-full-height dome-d-flex dome-align-center dome-justify-center"
        >
          <div className="widthdraw-flow-modal-body">{children}</div>
        </div>
      </div>
    </Portal>
  )
}

const DisclaimerConfirmed = ({ onClose, onSubmit }) => {
  return (
    <div className="dome-d-flex dome-flex-column dome-gap24">
      <div className="dome-d-flex dome-flex-column dome-gap12">
        <span>
          <i style={{ fontSize: '50px' }} className="fa-thin fa-circle-exclamation dome-color-red-alert"></i>
        </span>
        <span className="dome-h2 dome-color-red-alert">are you sure?</span>
      </div>
      <div className="dome-d-flex dome-flex-column dome-gap6">
        <span className="dome-p1 dome-color-dark-grey">
          Our representatives can help you make changes without impacting the rest of your submission.
        </span>
        <div className="dome-d-flex dome-flex-column">
          <span className="dome-p1 dome-color-dark-grey">
            For assistance, message the Application Processor(s) through the Requirements section or
          </span>
          <a href="mailto:info@domecile.com" className="dome-p1 dome-color-aqua">
            info@domecile.com
          </a>
        </div>
      </div>
      <div className="dome-d-flex dome-justify-between">
        <button
          type="button"
          onClick={onClose}
          className="dome-btn dome-btn-link dome-btn-link-med-grey dome-btn-link-chevron-left"
        >
          back
        </button>
        <button type="button" onClick={onSubmit} className="dome-btn dome-btn-base dome-btn-red-stroke">
          yes, withdraw application
        </button>
      </div>
    </div>
  )
}

const WidthdrawReason = ({ onClose, onSubmit, value, error, onChange }) => {
  return (
    <div className="dome-d-flex dome-flex-column dome-gap24">
      <div className="dome-d-flex dome-flex-column dome-gap12">
        <span>
          <i style={{ fontSize: '50px' }} className="fa-thin fa-circle-exclamation dome-color-red-alert"></i>
        </span>
        <span className="dome-h2 dome-color-red-alert">withdraw access?</span>
      </div>
      <div className="dome-d-flex dome-flex-column dome-gap12">
        <span className="dome-p1 dome-color-dark-grey">
          In a few words, please explain to the Application Processor (s) why you are withdrawing access.
        </span>
        <div className="dome-d-flex dome-flex-column dome-gap3">
          <textarea
            placeholder="type reason here"
            value={value}
            onChange={onChange}
            style={{
              minHeight: 80,
              padding: '6px 12px',
              background: 'var(--fill, #F9F9F9)',
              borderRadius: 6,
              border: '0.5px solid var(--med-grey, #898989)',
            }}
          />
          {error && (
            <div className="dome-d-flex dome-align-center dome-gap3">
              <i className="fa-solid fa-circle-exclamation dome-color-red-alert"></i>
              <span className="dome-p2 dome-color-red-alert">{error}</span>
            </div>
          )}
        </div>
      </div>
      <div className="dome-d-flex dome-justify-between">
        <button
          type="button"
          onClick={onClose}
          className="dome-btn dome-btn-link dome-btn-link-med-grey dome-btn-link-cancel"
        >
          cancel
        </button>
        <button type="button" onClick={onSubmit} className="dome-btn dome-btn-base dome-btn-red-stroke">
          withdraw access
        </button>
      </div>
    </div>
  )
}

export function WithdrawalFlow({
  onSubmit,
  onClose = () => {},
  withdrawText,
  labels = { tag: 'Package', processorTag: 'Application' },
}) {
  const [open, setOpen] = useState(false)
  const [step, setStep] = useState('confirm')
  const [reason, setReason] = useState('')
  const [error, setError] = useState(null)

  const close = () => {
    setOpen(false)
    setStep('confirm')
    setReason('')
    setError(null)
    onClose()
  }

  const handleWithdraw = async () => {
    if (!reason || !reason.trim()) {
      setError('This field cannot be blank.')
      return
    }
    try {
      await onSubmit(reason)
      promptWithdrawSuccess(labels)
    } catch {
      promptWithdrawError(labels)
    }
  }

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="dome-btn dome-btn-link">
        <div className="dome-d-flex dome-align-center dome-justify-center dome-gap6">
          <span className="dome-p2 dome-text-w500 dome-color-red-alert">{withdrawText}</span>
          <i className="fa-regular fa-arrow-right-long fa-xs dome-color-red-alert" />
        </div>
      </button>

      <WithdrawalModal open={open} onClose={close}>
        <>
          {step === 'confirm' ? (
            <DisclaimerConfirmed onClose={close} onSubmit={() => setStep('reason')} />
          ) : (
            <WidthdrawReason
              value={reason}
              error={error}
              onClose={close}
              onSubmit={handleWithdraw}
              onChange={(e) => {
                setError(null)
                setReason(e.target.value)
              }}
            />
          )}
        </>
      </WithdrawalModal>
    </>
  )
}

export default WithdrawalFlow
